import { HelpArticle } from './types';

// Helper to create article with auto-generated ID
const createArticle = (
  slug: string,
  title: string,
  summary: string,
  content: string,
  category: string,
  tags: string[],
  searchKeywords: string[],
  displayOrder: number
): HelpArticle => ({
  articleId: `art_${slug}`,
  slug,
  title,
  summary,
  content,
  category,
  tags,
  searchKeywords,
  displayOrder,
  isPublished: true,
});

// ============================================================================
// GETTING STARTED ARTICLES
// ============================================================================

const gettingStartedArticles: HelpArticle[] = [
  createArticle(
    'welcome-to-healiospace',
    'Welcome to HealioSpace',
    'Get started with your health coaching platform',
    `# Welcome to HealioSpace

HealioSpace is your comprehensive health coaching platform designed to help you manage clients, track progress, and deliver exceptional care.

## Quick Start Guide

1. **Complete your profile** - Add your practice information and credentials
2. **Add your first client** - Import or manually add clients to your practice
3. **Set up your calendar** - Configure your availability for appointments
4. **Explore features** - Discover messaging, telehealth, and progress tracking

## Key Features

### Client Management
Organize and track all your clients in one place. View health histories, track goals, and monitor progress over time.

### Secure Messaging
Communicate with clients through HIPAA-compliant messaging. Share documents, send reminders, and stay connected.

### Telehealth
Conduct video sessions directly within the platform. No additional software needed for you or your clients.

### Progress Tracking
Monitor client goals and outcomes with visual charts and milestone tracking.

### Content Library
Access training programs, meal plans, recipes, and educational resources to share with clients.

## Getting Help

- Press **F1** anywhere in the app to open this Help Center
- Use the search bar to find specific topics
- Browse categories for comprehensive guides

Need additional support? Contact us at support@healiospace.com`,
    'getting-started',
    ['welcome', 'intro', 'guide'],
    ['start', 'begin', 'new', 'setup', 'first', 'getting started'],
    1
  ),

  createArticle(
    'setting-up-your-practice',
    'Setting Up Your Practice',
    'Configure your practice profile and settings',
    `# Setting Up Your Practice

Before you start adding clients, configure your practice settings for the best experience.

## Practice Profile

1. Go to **Settings** from the sidebar
2. Select the **Practice** tab
3. Enter your practice details:
   - Practice name
   - Business address
   - Phone number
   - Website (optional)
4. Upload your practice logo
5. Click **Save Changes**

## Practitioner Information

1. Go to **Settings > Profile**
2. Complete your professional profile:
   - Full name and title
   - Professional credentials
   - Specialty areas
   - Bio/description
3. Upload a professional photo
4. Click **Save**

## Working Hours

1. Go to **Calendar** from the sidebar
2. Click the **Settings** icon
3. Set your working hours for each day
4. Configure time zones if needed
5. Set appointment buffer times

## Notification Preferences

1. Go to **Settings > Notifications**
2. Choose how you want to be notified:
   - Email notifications
   - In-app alerts
   - Message notifications
3. Set quiet hours if desired

Your practice is now ready for clients!`,
    'getting-started',
    ['setup', 'practice', 'profile'],
    ['configure', 'settings', 'account', 'practice', 'logo'],
    2
  ),

  createArticle(
    'understanding-the-dashboard',
    'Understanding the Dashboard',
    'Navigate and use the main dashboard effectively',
    `# Understanding the Dashboard

The dashboard is your command center for managing your health coaching practice.

## Navigation

### Sidebar Menu
The left sidebar provides quick access to all major features:
- **Dashboard** - Overview and quick actions
- **Clients** - Manage your client roster
- **Calendar** - View and manage appointments
- **Messages** - Secure client communications
- **Content** - Training programs, recipes, and resources
- **Reports** - Analytics and client progress
- **Settings** - Account and practice configuration

### Quick Actions
The dashboard displays shortcuts to common tasks:
- Add new client
- Schedule appointment
- Send message
- Create content

## Dashboard Widgets

### Today's Schedule
See your upcoming appointments at a glance. Click any appointment to view details or start a session.

### Recent Messages
View and respond to recent client messages without leaving the dashboard.

### Client Activity
Monitor recent client check-ins, goal progress, and engagement metrics.

### Quick Stats
View key metrics including:
- Total active clients
- Appointments this week
- Unread messages
- Pending tasks

## Customization

You can customize your dashboard layout in **Settings > Preferences** to show the widgets most relevant to your workflow.`,
    'getting-started',
    ['dashboard', 'navigation', 'overview'],
    ['home', 'main', 'navigate', 'menu', 'sidebar'],
    3
  ),

  createArticle(
    'subscription-tiers-explained',
    'Subscription Tiers Explained',
    'Understand the differences between Free, Plus, Premium, and Enterprise plans',
    `# Subscription Tiers Explained

HealioSpace offers flexible subscription plans to match your practice size and needs.

## Plan Comparison

### Free Plan
Perfect for getting started or small practices:
- Up to 5 active clients
- Basic messaging
- Calendar management
- Standard support
- 1GB file storage

### Plus Plan
For growing practices:
- Up to 25 active clients
- Telehealth video sessions
- Custom forms
- Training program builder
- Email support
- 10GB file storage

### Premium Plan
For established practices:
- Up to 100 active clients
- All Plus features
- AI-powered meal plans
- Advanced reporting
- Priority support
- 50GB file storage
- Custom branding

### Enterprise Plan
For large organizations:
- Unlimited clients
- All Premium features
- Multi-practitioner support
- API access
- Dedicated account manager
- Unlimited storage
- HIPAA BAA included
- Custom integrations

## Upgrading Your Plan

1. Go to **Settings > Billing**
2. Click **Change Plan**
3. Select your new plan
4. Confirm payment details
5. Your new features are available immediately

## Downgrading

If you need to downgrade:
1. Go to **Settings > Billing**
2. Click **Change Plan**
3. Select a lower tier
4. Changes take effect at your next billing cycle

Note: Ensure you're within the client limits before downgrading.`,
    'getting-started',
    ['plans', 'pricing', 'features'],
    ['subscription', 'tier', 'free', 'plus', 'premium', 'enterprise', 'upgrade'],
    4
  ),

  createArticle(
    'keyboard-shortcuts',
    'Keyboard Shortcuts',
    'Speed up your workflow with keyboard shortcuts',
    `# Keyboard Shortcuts

Master these shortcuts to navigate HealioSpace efficiently.

## Global Shortcuts

| Shortcut | Action |
|----------|--------|
| **F1** | Open Help Center |
| **Ctrl/Cmd + K** | Quick search |
| **Ctrl/Cmd + N** | New client |
| **Ctrl/Cmd + Shift + N** | New appointment |
| **Ctrl/Cmd + M** | New message |

## Navigation

| Shortcut | Action |
|----------|--------|
| **G then D** | Go to Dashboard |
| **G then C** | Go to Clients |
| **G then A** | Go to Calendar |
| **G then M** | Go to Messages |
| **G then S** | Go to Settings |

## Calendar

| Shortcut | Action |
|----------|--------|
| **T** | Jump to today |
| **Left/Right** | Previous/next period |
| **D** | Day view |
| **W** | Week view |
| **M** | Month view |

## Messages

| Shortcut | Action |
|----------|--------|
| **Enter** | Send message |
| **Shift + Enter** | New line |
| **Escape** | Close conversation |

## Client Profile

| Shortcut | Action |
|----------|--------|
| **E** | Edit client |
| **N** | Add note |
| **A** | Schedule appointment |
| **M** | Send message |

## Tips

- Press **?** on any page to see available shortcuts
- Shortcuts can be customized in **Settings > Preferences**
- Most shortcuts work with both Ctrl (Windows) and Cmd (Mac)`,
    'getting-started',
    ['shortcuts', 'keyboard', 'productivity'],
    ['keys', 'hotkeys', 'quick', 'fast', 'efficiency'],
    5
  ),
];

// ============================================================================
// MANAGING CLIENTS ARTICLES
// ============================================================================

const clientsArticles: HelpArticle[] = [
  createArticle(
    'adding-clients',
    'Adding Clients',
    'Learn how to add and manage clients in your practice',
    `# Adding Clients

There are several ways to add clients to your practice.

## Manual Entry

1. Go to **Clients** from the sidebar
2. Click **Add Client** button
3. Enter the client's information:
   - First and last name
   - Email address
   - Phone number (optional)
   - Date of birth (optional)
4. Click **Create Client**

## Invite via Email

Send an invitation for clients to create their own account:

1. Click **Invite Client**
2. Enter the client's email address
3. Customize the invitation message (optional)
4. Click **Send Invitation**

The client will receive an email with a link to:
- Create their account
- Complete their profile
- Accept your practice's terms

## Client Portal

Once added, clients can access their portal to:
- View upcoming appointments
- Send secure messages
- Complete forms and assessments
- Track their progress
- Access shared resources

## Client Limits

Remember your plan's client limits:
- **Free**: 5 active clients
- **Plus**: 25 active clients
- **Premium**: 100 active clients
- **Enterprise**: Unlimited

Archived clients don't count toward your limit.`,
    'clients',
    ['add', 'create', 'new', 'client'],
    ['patient', 'user', 'invite', 'onboard', 'register'],
    1
  ),

  createArticle(
    'client-profiles',
    'Managing Client Profiles',
    'View and update client information and health data',
    `# Managing Client Profiles

Each client profile contains comprehensive information to help you provide personalized care.

## Profile Sections

### Basic Information
- Name and contact details
- Demographics
- Emergency contact
- Insurance information (if applicable)

### Health Information
- Health history
- Current conditions
- Medications
- Allergies
- Goals and objectives

### Activity Timeline
View a chronological history of:
- Appointments
- Messages
- Form submissions
- Progress updates
- Notes

## Editing Client Information

1. Go to the client's profile
2. Click **Edit** or the pencil icon
3. Update the relevant fields
4. Click **Save Changes**

## Client Tags

Organize clients with custom tags:
1. Open client profile
2. Click **Add Tag**
3. Select existing or create new tag
4. Use tags to filter your client list

## Archiving Clients

When a client completes their program or becomes inactive:
1. Open their profile
2. Click **More Options** (three dots)
3. Select **Archive Client**
4. Confirm the action

Archived clients can be restored at any time from the archived clients view.`,
    'clients',
    ['profile', 'information', 'details'],
    ['edit', 'update', 'view', 'data', 'health history'],
    2
  ),

  createArticle(
    'client-progress-tracking',
    'Tracking Client Progress',
    'Monitor and track client health goals and outcomes',
    `# Tracking Client Progress

Keep track of your clients' health journeys with progress tracking tools.

## Setting Goals

1. Open the client's profile
2. Go to the **Goals** tab
3. Click **Add Goal**
4. Configure the goal:
   - Goal type (weight, nutrition, fitness, habit, custom)
   - Target value
   - Timeline
   - Milestones (optional)
5. Click **Create Goal**

## Goal Types

### Weight Goals
Track weight changes over time with visual charts.

### Nutrition Goals
Monitor caloric intake, macros, or specific dietary targets.

### Fitness Goals
Track workout frequency, duration, or specific metrics.

### Habit Goals
Build new habits with streak tracking and reminders.

### Custom Goals
Create goals for any metric you want to track.

## Logging Progress

### Client Self-Logging
Clients can log their own progress through their portal:
- Daily check-ins
- Metric updates
- Photo progress
- Journal entries

### Practitioner Updates
You can also update progress:
1. Go to client profile
2. Select the goal
3. Click **Add Entry**
4. Enter the value and date
5. Add notes if needed

## Progress Views

- **Charts**: Visual progress over time
- **Milestones**: Track achievement of key milestones
- **Trends**: See patterns and averages
- **Reports**: Export progress summaries

## Notifications

Set up alerts for:
- Missed check-ins
- Goals at risk
- Milestone achievements
- Exceptional progress`,
    'clients',
    ['progress', 'goals', 'tracking'],
    ['metrics', 'health', 'monitor', 'outcomes', 'weight', 'fitness'],
    3
  ),

  createArticle(
    'client-documents',
    'Client Documents and Files',
    'Share and manage documents with clients securely',
    `# Client Documents and Files

Securely share and store documents with your clients.

## Uploading Documents

1. Go to the client's profile
2. Select the **Documents** tab
3. Click **Upload** or drag and drop files
4. Add a description (optional)
5. Choose visibility:
   - **Private**: Only you can see
   - **Shared**: Client can also view

## Supported File Types

- Documents: PDF, DOC, DOCX, TXT
- Spreadsheets: XLS, XLSX, CSV
- Images: JPG, PNG, GIF
- Maximum file size: 25MB per file

## Organizing Documents

### Folders
Create folders to organize documents:
1. Click **New Folder**
2. Name the folder
3. Drag documents into folders

### Categories
Tag documents by type:
- Intake forms
- Assessments
- Progress photos
- Meal plans
- Educational resources

## Sharing with Clients

Documents marked as "Shared" appear in the client's portal. You can:
- Share existing documents
- Request document uploads from clients
- Set expiration dates on shared links

## Document Security

All documents are:
- Encrypted at rest and in transit
- HIPAA compliant
- Access logged for compliance
- Automatically backed up

## Storage Limits

- **Free**: 1GB
- **Plus**: 10GB
- **Premium**: 50GB
- **Enterprise**: Unlimited`,
    'clients',
    ['documents', 'files', 'upload'],
    ['share', 'pdf', 'storage', 'download', 'attachment'],
    4
  ),

  createArticle(
    'client-notes',
    'Client Notes and Session Documentation',
    'Document client interactions and session notes',
    `# Client Notes and Session Documentation

Keep detailed records of your client interactions with notes.

## Adding Notes

1. Open the client's profile
2. Go to the **Notes** tab
3. Click **Add Note**
4. Select note type:
   - General note
   - Session note
   - Progress note
   - Private note
5. Enter your note content
6. Click **Save**

## Note Types

### General Notes
Quick notes for reminders or observations.

### Session Notes
Document appointment details:
- Session date and duration
- Topics discussed
- Client feedback
- Action items
- Follow-up needed

### Progress Notes
Record progress updates:
- Measurements
- Achievements
- Challenges
- Adjustments to plan

### Private Notes
Notes only visible to you (not shared with client or other practitioners).

## Note Templates

Save time with templates:
1. Go to **Settings > Templates**
2. Click **Create Note Template**
3. Enter template content with placeholders
4. Save template

Use templates when creating new notes.

## Searching Notes

Find notes quickly:
- Use the search bar in the Notes tab
- Filter by date range
- Filter by note type
- Sort by date or relevance

## Exporting Notes

Export client notes for records:
1. Go to Notes tab
2. Click **Export**
3. Select date range
4. Choose format (PDF or CSV)
5. Download file`,
    'clients',
    ['notes', 'documentation', 'records'],
    ['session', 'document', 'write', 'record', 'soap'],
    5
  ),

  createArticle(
    'client-filters-search',
    'Filtering and Searching Clients',
    'Find and organize clients efficiently',
    `# Filtering and Searching Clients

Quickly find clients with powerful search and filtering tools.

## Quick Search

1. Click the search bar or press **Ctrl/Cmd + K**
2. Type client name, email, or phone
3. Select from results

## Advanced Filters

Click **Filters** on the Clients page to filter by:

### Status
- Active clients
- Archived clients
- Pending invitations

### Tags
Filter by custom tags you've created.

### Subscription Tier
For practices with different client tiers.

### Last Activity
- Active in last 7 days
- Active in last 30 days
- Inactive for 30+ days

### Appointment Status
- Has upcoming appointment
- Needs scheduling
- No recent appointments

### Program
Filter by assigned training programs or meal plans.

## Saved Filters

Save frequently used filter combinations:
1. Apply your filters
2. Click **Save Filter**
3. Name your filter
4. Access saved filters from the dropdown

## Sorting Options

Sort your client list by:
- Name (A-Z or Z-A)
- Last activity date
- Next appointment
- Date added
- Progress status

## Bulk Actions

Select multiple clients to:
- Send group messages
- Assign to programs
- Add/remove tags
- Export data
- Archive clients`,
    'clients',
    ['search', 'filter', 'organize'],
    ['find', 'sort', 'list', 'bulk', 'group'],
    6
  ),
];

// ============================================================================
// APPOINTMENTS ARTICLES
// ============================================================================

const appointmentsArticles: HelpArticle[] = [
  createArticle(
    'scheduling-appointments',
    'Scheduling Appointments',
    'Create and manage client appointments',
    `# Scheduling Appointments

Easily schedule and manage all your client appointments.

## Creating an Appointment

### From Calendar
1. Go to **Calendar** from the sidebar
2. Click on a time slot
3. Fill in appointment details:
   - Select client
   - Choose appointment type
   - Set duration
   - Add notes (optional)
4. Click **Create**

### From Client Profile
1. Open the client's profile
2. Click **Schedule Appointment**
3. Select date and time
4. Choose appointment type
5. Confirm details

## Appointment Types

Configure your appointment types in **Settings > Calendar**:

- **Initial Consultation** - First meeting with new clients
- **Follow-up** - Regular check-in sessions
- **Telehealth** - Video appointments
- **Group Session** - Multiple clients
- **Quick Check-in** - Brief updates

Each type can have:
- Custom duration
- Default location
- Preparation instructions
- Follow-up templates

## Appointment Details

When creating an appointment, you can add:
- Location (in-person address or video link)
- Preparation notes for the client
- Internal notes (not visible to client)
- Attached resources or forms

## Managing Appointments

### Reschedule
1. Click on the appointment
2. Select **Reschedule**
3. Choose new date/time
4. Optionally notify the client

### Cancel
1. Click on the appointment
2. Select **Cancel**
3. Provide cancellation reason (optional)
4. Choose to notify client`,
    'appointments',
    ['schedule', 'book', 'calendar'],
    ['appointment', 'meeting', 'session', 'create', 'new'],
    1
  ),

  createArticle(
    'calendar-views',
    'Calendar Views and Navigation',
    'Navigate your calendar and manage your schedule',
    `# Calendar Views and Navigation

The calendar provides multiple views to manage your schedule effectively.

## Calendar Views

### Day View
See detailed hourly breakdown of a single day:
- View all appointment details
- See available time slots
- Manage overlapping events

Press **D** to switch to Day view.

### Week View
Overview of your entire week:
- Compare days side by side
- Spot scheduling patterns
- Plan ahead efficiently

Press **W** to switch to Week view.

### Month View
Bird's eye view of the month:
- See appointment density
- Identify busy periods
- Plan vacations or time off

Press **M** to switch to Month view.

## Navigation

- **Today**: Click "Today" button or press **T**
- **Previous/Next**: Use arrows or **Left/Right** keys
- **Specific Date**: Click the date header to open date picker

## Color Coding

Appointments are color-coded by type:
- Initial consultations (blue)
- Follow-ups (green)
- Telehealth (purple)
- Group sessions (orange)
- Blocked time (gray)

Customize colors in **Settings > Calendar**.

## Working Hours

Your working hours appear with a white background. Non-working hours are grayed out. Set your hours in **Settings > Calendar > Working Hours**.

## Time Zones

HealioSpace displays times in your local timezone by default. For clients in different zones:
1. Go to **Settings > Calendar**
2. Enable **Show client timezone**
3. Times will display in both zones`,
    'appointments',
    ['calendar', 'view', 'navigation'],
    ['day', 'week', 'month', 'schedule', 'date'],
    2
  ),

  createArticle(
    'appointment-reminders',
    'Appointment Reminders',
    'Configure automatic reminders for appointments',
    `# Appointment Reminders

Automatic reminders help reduce no-shows and keep clients engaged.

## Default Reminders

By default, reminders are sent:
- 24 hours before the appointment (email)
- 1 hour before the appointment (email)

## Customizing Reminders

1. Go to **Settings > Calendar**
2. Select **Reminders** tab
3. Configure your reminder schedule

### Reminder Options

**Timing**
- 1 week before
- 3 days before
- 24 hours before
- 2 hours before
- 1 hour before
- 30 minutes before

**Delivery Method**
- Email
- SMS (Premium and Enterprise plans)
- In-app notification

**Content**
Customize reminder messages with variables:
- {client_name}
- {appointment_date}
- {appointment_time}
- {appointment_type}
- {practitioner_name}
- {location}

## Per-Appointment Reminders

Override default reminders for specific appointments:
1. Open the appointment
2. Click **Edit**
3. Scroll to **Reminders**
4. Customize for this appointment

## Reminder Templates

Create templates for different appointment types:
1. Go to **Settings > Templates**
2. Click **New Reminder Template**
3. Configure timing and message
4. Assign to appointment types

## Tracking Reminders

View reminder status in the appointment details:
- Scheduled (pending)
- Sent (delivered)
- Opened (if tracking enabled)
- Failed (delivery issue)`,
    'appointments',
    ['reminders', 'notifications', 'alerts'],
    ['email', 'sms', 'notify', 'no-show', 'confirmation'],
    3
  ),

  createArticle(
    'recurring-appointments',
    'Recurring Appointments',
    'Set up recurring appointment schedules',
    `# Recurring Appointments

Create recurring appointments for regular client sessions.

## Creating Recurring Appointments

1. Create a new appointment or edit existing
2. Click **Make Recurring**
3. Set recurrence pattern:
   - Daily
   - Weekly (select days)
   - Bi-weekly
   - Monthly
4. Set end condition:
   - After X occurrences
   - Until specific date
   - No end date
5. Click **Create Series**

## Recurrence Patterns

### Weekly
Select specific days of the week:
- Every Monday at 10am
- Every Tuesday and Thursday at 2pm

### Bi-weekly
Every two weeks on selected days.

### Monthly
Options include:
- Same date each month (e.g., 15th)
- Same day pattern (e.g., second Tuesday)

## Managing Recurring Series

### Edit Single Occurrence
1. Click on the specific appointment
2. Choose **Edit This Appointment**
3. Make changes
4. Changes apply only to this instance

### Edit Entire Series
1. Click on any appointment in the series
2. Choose **Edit Series**
3. Make changes
4. Choose to apply to:
   - All appointments
   - This and future appointments

### Cancel Occurrence
1. Click on the appointment
2. Select **Cancel This Appointment**
3. Other appointments in series remain

### Cancel Series
1. Click on any appointment
2. Select **Cancel Series**
3. Choose:
   - Cancel all
   - Cancel this and future only`,
    'appointments',
    ['recurring', 'repeat', 'series'],
    ['regular', 'weekly', 'monthly', 'schedule'],
    4
  ),

  createArticle(
    'client-self-scheduling',
    'Client Self-Scheduling',
    'Allow clients to book their own appointments',
    `# Client Self-Scheduling

Enable clients to book appointments directly through their portal.

## Enabling Self-Scheduling

1. Go to **Settings > Calendar**
2. Select **Self-Scheduling** tab
3. Toggle **Enable Client Booking**
4. Configure your preferences

## Configuration Options

### Available Appointment Types
Select which appointment types clients can book:
- Follow-up sessions only
- All appointment types
- Specific types

### Booking Window
Set how far in advance clients can book:
- Minimum notice (e.g., 24 hours)
- Maximum advance (e.g., 30 days)

### Available Hours
Choose when clients can book:
- Use your working hours
- Set custom availability
- Block specific times

### Buffer Time
Require buffer time between appointments:
- Before appointments
- After appointments
- Prevent back-to-back bookings

## Approval Settings

Choose whether bookings are:
- **Automatic**: Instantly confirmed
- **Pending**: Require your approval

## Sharing Your Booking Link

Get your booking link:
1. Go to **Settings > Calendar > Self-Scheduling**
2. Copy your unique booking URL
3. Share via email, website, or messages

## Client Experience

When clients use self-scheduling:
1. View your available times
2. Select appointment type
3. Choose date and time
4. Add notes (optional)
5. Confirm booking
6. Receive confirmation email`,
    'appointments',
    ['self-scheduling', 'booking', 'online'],
    ['client booking', 'schedule online', 'availability', 'book'],
    5
  ),

  createArticle(
    'appointment-notes-documentation',
    'Appointment Notes and Documentation',
    'Document appointments and maintain records',
    `# Appointment Notes and Documentation

Maintain comprehensive records of all client appointments.

## Pre-Appointment Notes

Add notes before the appointment:
1. Open the appointment
2. Add **Preparation Notes**:
   - Topics to discuss
   - Forms to complete
   - Resources to share

## During Appointment

Take notes during the session:
1. Click **Start Session** on the appointment
2. Use the notes panel to document:
   - Discussion points
   - Client feedback
   - Observations
   - Decisions made

## Post-Appointment Documentation

After the appointment:
1. Open the appointment
2. Click **Add Session Notes**
3. Document:
   - Summary of session
   - Client progress
   - Action items
   - Follow-up needed
4. Set session status:
   - Completed
   - Client no-show
   - Cancelled late

## Session Note Templates

Create templates for consistent documentation:

### SOAP Notes
- **Subjective**: Client's reported symptoms/concerns
- **Objective**: Observable/measurable findings
- **Assessment**: Your evaluation
- **Plan**: Next steps and recommendations

### Progress Notes
- Current status
- Changes since last visit
- Goals progress
- Adjustments to plan

## Linking to Client Records

Session notes automatically:
- Appear in client's activity timeline
- Link to appointment details
- Are searchable in client notes

## Compliance Considerations

For HIPAA compliance:
- Notes are encrypted at rest
- Access is logged
- Retention policies can be configured
- Export capabilities for records requests`,
    'appointments',
    ['notes', 'documentation', 'records'],
    ['session notes', 'soap', 'document', 'record'],
    6
  ),
];

// ============================================================================
// MESSAGING ARTICLES
// ============================================================================

const messagingArticles: HelpArticle[] = [
  createArticle(
    'secure-messaging',
    'Secure Messaging',
    'Communicate securely with clients',
    `# Secure Messaging

HealioSpace provides HIPAA-compliant messaging for all client communications.

## Sending Messages

1. Go to **Messages** from the sidebar
2. Select a client or click **New Message**
3. Type your message
4. Click **Send** or press **Enter**

## Message Features

### Text Messages
Send plain text messages for quick communication.

### Rich Formatting
Use markdown for formatting:
- **Bold**: \`**text**\`
- *Italic*: \`*text*\`
- Lists with - or *
- Links: \`[text](url)\`

### File Attachments
1. Click the attachment icon
2. Select files (max 25MB each)
3. Supported: PDF, DOC, images, spreadsheets
4. Files are securely stored

### Quick Replies
Save time with message templates:
1. Click the templates icon
2. Select a saved response
3. Edit if needed before sending

## Read Receipts

See when messages are read:
- Single check: Sent
- Double check: Delivered
- Blue checks: Read

Disable read receipts in **Settings > Privacy**.

## Security Features

All messages are:
- End-to-end encrypted
- HIPAA compliant
- Stored securely
- Audit logged

## Notifications

Configure message notifications:
1. Go to **Settings > Notifications**
2. Set preferences for:
   - Email alerts
   - In-app notifications
   - Sound alerts`,
    'messaging',
    ['message', 'chat', 'communication'],
    ['secure', 'hipaa', 'send', 'text', 'communicate'],
    1
  ),

  createArticle(
    'message-templates',
    'Message Templates',
    'Create and use message templates for efficiency',
    `# Message Templates

Save time with reusable message templates.

## Creating Templates

1. Go to **Settings > Templates**
2. Click **New Message Template**
3. Enter template details:
   - Template name
   - Category (optional)
   - Message content
4. Add variables for personalization
5. Click **Save**

## Available Variables

Personalize templates with variables:
- \`{client_name}\` - Client's first name
- \`{full_name}\` - Client's full name
- \`{next_appointment}\` - Next scheduled appointment
- \`{practitioner_name}\` - Your name
- \`{practice_name}\` - Practice name
- \`{portal_link}\` - Client portal URL

## Template Categories

Organize templates by purpose:
- Welcome messages
- Appointment reminders
- Check-in messages
- Resource sharing
- Follow-up messages

## Using Templates

### In Messages
1. Open conversation with client
2. Click template icon (or press \`/\`)
3. Search or browse templates
4. Select template
5. Edit if needed
6. Send

### Bulk Messaging
1. Select multiple clients
2. Click **Send Message**
3. Choose template
4. Review and send

## Sharing Templates

For multi-practitioner practices:
1. Create template
2. Set visibility:
   - Private (only you)
   - Practice (all practitioners)
3. Templates sync across the practice`,
    'messaging',
    ['templates', 'quick-replies', 'saved'],
    ['canned', 'response', 'reusable', 'snippet'],
    2
  ),

  createArticle(
    'group-messaging',
    'Group Messaging',
    'Send messages to multiple clients at once',
    `# Group Messaging

Communicate with multiple clients efficiently through group messaging.

## Creating a Group Message

1. Go to **Messages**
2. Click **New Message**
3. Select **Group Message**
4. Add recipients:
   - Search for clients
   - Select from list
   - Use client tags
5. Compose your message
6. Click **Send**

## Recipient Selection

### Manual Selection
Search and add clients individually.

### By Tags
Select all clients with specific tags:
- "Active Program"
- "Group Class"
- Custom tags

### By Filter
Use advanced filters:
- Last activity
- Appointment status
- Subscription tier

## Group Message Options

### BCC Mode (Default)
- Recipients don't see each other
- Each gets individual message
- Replies come only to you

### Discussion Group
- All members can see each other
- Threaded conversation
- Great for group programs

## Best Practices

- Use templates for consistency
- Personalize with variables
- Segment your audience
- Track delivery and opens
- Respect message frequency

## Compliance Note

Group messages are still HIPAA compliant:
- BCC mode protects privacy
- Each message is encrypted
- Delivery is logged`,
    'messaging',
    ['group', 'bulk', 'broadcast'],
    ['multiple', 'mass', 'batch', 'send all'],
    3
  ),

  createArticle(
    'message-attachments',
    'Message Attachments and Files',
    'Share files securely through messages',
    `# Message Attachments and Files

Share documents, images, and resources securely through messaging.

## Sending Attachments

1. Open a conversation
2. Click the attachment icon (paperclip)
3. Choose files or drag and drop
4. Add a message (optional)
5. Click **Send**

## Supported File Types

### Documents
- PDF files
- Word documents (DOC, DOCX)
- Text files (TXT)
- Spreadsheets (XLS, XLSX, CSV)

### Images
- JPEG/JPG
- PNG
- GIF
- HEIC (converted automatically)

### Other
- ZIP archives (max 50MB)
- Audio files (MP3, WAV)
- Video files (MP4, MOV) - Premium only

## File Size Limits

- Individual file: 25MB
- Total per message: 50MB
- Monthly upload: Based on storage plan

## Sharing from Content Library

1. Click the attachment icon
2. Select **From Library**
3. Browse your content:
   - Meal plans
   - Training programs
   - Educational resources
4. Select items to share
5. Send

## Security

All attachments are:
- Scanned for viruses
- Encrypted in transit
- Encrypted at rest
- Access controlled
- Audit logged

## Viewing Attachments

Recipients can:
- Preview in browser
- Download to device
- Files available until deleted`,
    'messaging',
    ['attachments', 'files', 'share'],
    ['upload', 'document', 'pdf', 'image', 'send file'],
    4
  ),

  createArticle(
    'message-search-archive',
    'Searching and Archiving Messages',
    'Find and manage your message history',
    `# Searching and Archiving Messages

Efficiently manage your message history with search and archiving tools.

## Searching Messages

### Quick Search
1. Go to **Messages**
2. Use the search bar at the top
3. Type keywords, client name, or content
4. Press **Enter** to search

### Advanced Search

Click **Advanced** for more options:
- **Date Range**: Filter by time period
- **Client**: Specific client conversations
- **Has Attachment**: Messages with files
- **Unread Only**: Unread messages

### Search Tips
- Use quotes for exact phrases: "meal plan"
- Search by file name: "nutrition.pdf"
- Combine filters for precise results

## Message Organization

### Starring Messages
Mark important messages:
1. Hover over message
2. Click the star icon
3. View starred messages in filter

### Pinning Conversations
Keep important conversations at top:
1. Right-click conversation
2. Select **Pin**
3. Pinned chats stay at top

## Archiving

### Archive Conversations
1. Right-click conversation
2. Select **Archive**
3. Conversation moves to Archive

### Viewing Archives
1. Click **Filters**
2. Select **Show Archived**
3. View archived conversations

### Unarchiving
1. Open archived conversation
2. Click **Unarchive**
3. Returns to main inbox

## Message Retention

Messages are retained based on your plan and settings:
- Default: Indefinitely
- Custom retention policies (Enterprise)
- Export before deletion

## Exporting Messages

For records or compliance:
1. Open conversation
2. Click **More Options**
3. Select **Export**
4. Choose format (PDF or JSON)
5. Select date range
6. Download`,
    'messaging',
    ['search', 'archive', 'history'],
    ['find', 'organize', 'filter', 'export', 'backup'],
    5
  ),
];

// ============================================================================
// TELEHEALTH ARTICLES
// ============================================================================

const telehealthArticles: HelpArticle[] = [
  createArticle(
    'starting-video-session',
    'Starting a Video Session',
    'Launch and manage telehealth appointments',
    `# Starting a Video Session

Conduct virtual appointments with built-in video conferencing.

## Before Your Session

### Technical Check
1. Test your camera and microphone
2. Check your internet connection
3. Ensure good lighting
4. Choose a quiet, private space

### Preparation
1. Review client's profile and history
2. Have relevant documents ready
3. Prepare any resources to share
4. Close unnecessary applications

## Starting a Session

### From Calendar
1. Go to your **Calendar**
2. Find the scheduled telehealth appointment
3. Click **Start Session**
4. Wait in the virtual waiting room

### From Appointment
1. Open the appointment details
2. Click **Start Video Session**
3. The video room opens automatically

## Waiting for Client

When you start, you'll enter a waiting room:
- See when client joins
- Send a reminder if late
- Access session notes

## During the Session

### Video Controls
- **Mute/Unmute**: Toggle microphone
- **Camera On/Off**: Toggle video
- **Screen Share**: Share your screen
- **End Call**: Leave the session

### Additional Features
- In-call chat messaging
- Session timer
- Note-taking panel
- Resource sharing

## Technical Requirements

### For Practitioners
- Modern browser (Chrome, Firefox, Safari, Edge)
- Webcam and microphone
- Stable internet (5 Mbps+ recommended)

### For Clients
- Modern browser or mobile app
- No software installation required
- Works on desktop, tablet, or phone`,
    'telehealth',
    ['video', 'session', 'virtual'],
    ['call', 'zoom', 'meeting', 'conference', 'start'],
    1
  ),

  createArticle(
    'telehealth-settings',
    'Telehealth Settings',
    'Configure your telehealth preferences',
    `# Telehealth Settings

Customize your video session experience.

## Accessing Settings

1. Go to **Settings** from sidebar
2. Select **Telehealth** tab
3. Configure your preferences

## Video Settings

### Default Camera
Select your preferred camera if you have multiple.

### Video Quality
- **Auto**: Adjusts to network conditions
- **HD**: High definition (requires good connection)
- **Standard**: Lower bandwidth usage

### Background
- No background
- Blur background
- Virtual backgrounds (Premium)

## Audio Settings

### Microphone
Select your preferred input device.

### Speaker
Choose output device for audio.

### Echo Cancellation
Enable to reduce echo (recommended).

### Noise Suppression
Filter background noise.

## Waiting Room

### Enabled/Disabled
Control whether clients enter a waiting room.

### Custom Message
Set a welcome message for waiting clients.

### Auto-Admit
Automatically admit clients (optional).

## Recording

### Enable Recording
Allow session recording (requires consent).

### Auto-Record
Automatically record all sessions.

### Storage Location
Choose where recordings are stored.

### Consent Notice
Customize the recording consent message.

## Client Settings

### Allow Screen Sharing
Let clients share their screen.

### Allow Chat
Enable in-call messaging.

### Client Video Default
Whether client video starts on/off.`,
    'telehealth',
    ['settings', 'configuration', 'video'],
    ['configure', 'camera', 'microphone', 'preferences'],
    2
  ),

  createArticle(
    'screen-sharing',
    'Screen Sharing in Sessions',
    'Share your screen during video sessions',
    `# Screen Sharing in Sessions

Share your screen to present resources, demonstrate tools, or review documents together.

## Starting Screen Share

1. During a video session, click **Share Screen**
2. Choose what to share:
   - Entire screen
   - Specific window
   - Browser tab
3. Click **Share**

## Sharing Options

### Entire Screen
Share everything on your display:
- Full desktop view
- Multiple windows
- System notifications (be careful!)

### Application Window
Share a specific application:
- Only that window is visible
- Other apps remain private
- Best for focused presentations

### Browser Tab
Share a single browser tab:
- Include tab audio (optional)
- Best for web-based content
- Other tabs stay private

## What to Share

### Educational Content
- Nutrition guides
- Exercise videos
- Wellness resources

### Progress Charts
- Goal tracking visuals
- Measurement trends
- Comparison charts

### Meal Plans & Recipes
- Browse together
- Make adjustments
- Discuss options

### Documents
- Forms to complete
- Reports to review
- Action plans

## Best Practices

- Close personal tabs before sharing
- Disable notifications
- Prepare content in advance
- Use window share when possible
- Test sharing before sessions

## Stopping Screen Share

1. Click **Stop Sharing** in the control bar
2. Or click the screen share icon again
3. You return to video view`,
    'telehealth',
    ['screen', 'share', 'present'],
    ['screen sharing', 'presentation', 'display', 'show'],
    3
  ),

  createArticle(
    'telehealth-troubleshooting',
    'Telehealth Troubleshooting',
    'Solve common video session issues',
    `# Telehealth Troubleshooting

Solutions for common video session issues.

## Video Issues

### Camera Not Working
1. Check if camera is connected
2. Verify browser permissions:
   - Click lock icon in address bar
   - Ensure camera is "Allowed"
3. Close other apps using camera
4. Try a different browser
5. Restart browser

### Video is Blurry
1. Check internet speed
2. Reduce video quality in settings
3. Close bandwidth-heavy apps
4. Move closer to router
5. Try wired connection

### Black Screen
1. Refresh the page
2. Check camera permissions
3. Ensure camera isn't covered
4. Update browser
5. Try incognito mode

## Audio Issues

### No Sound
1. Check volume settings
2. Verify correct output device
3. Test speakers outside app
4. Check browser permissions
5. Restart browser

### Echo
1. Use headphones
2. Lower speaker volume
3. Enable echo cancellation
4. Increase distance from mic
5. Check for multiple audio sources

### Choppy Audio
1. Check internet connection
2. Close other applications
3. Reduce video quality
4. Use wired connection
5. Move closer to router

## Connection Issues

### Can't Connect
1. Refresh the page
2. Check internet connection
3. Disable VPN if using
4. Try different browser
5. Clear browser cache

### Frequent Disconnections
1. Test internet stability
2. Switch to wired connection
3. Move closer to router
4. Reduce video quality
5. Close background apps

## Client Issues

If your client has issues:
1. Send troubleshooting guide
2. Suggest trying mobile app
3. Test with audio-only
4. Reschedule if needed
5. Consider phone call backup`,
    'telehealth',
    ['troubleshooting', 'issues', 'problems'],
    ['fix', 'error', 'not working', 'help', 'camera', 'audio'],
    4
  ),

  createArticle(
    'session-recording',
    'Recording Video Sessions',
    'Record and manage telehealth session recordings',
    `# Recording Video Sessions

Record sessions for documentation, review, or client access.

## Recording Requirements

### Consent
- Always get client consent before recording
- Consent notice displays when recording starts
- Client must acknowledge to continue

### Plan Requirements
- Recording available on Plus and above
- Storage counts toward your limit

## Starting a Recording

1. During a video session
2. Click the **Record** button
3. Client sees consent notice
4. Client confirms consent
5. Recording begins (indicator shows)

## During Recording

### What's Captured
- Video from all participants
- Audio from all participants
- Screen shares
- In-call chat (optional)

### Recording Indicator
Both you and client see:
- Red recording dot
- Recording duration
- Option to pause/stop

### Pausing
1. Click **Pause Recording**
2. Recording pauses (not stopped)
3. Click **Resume** to continue

## Stopping Recording

1. Click **Stop Recording**
2. Or end the session
3. Recording is processed
4. Available in a few minutes

## Managing Recordings

### Viewing Recordings
1. Go to the appointment
2. Click **Session Recording**
3. Or find in **Content > Recordings**

### Sharing with Client
1. Open the recording
2. Click **Share with Client**
3. Client can view in their portal

### Downloading
1. Open recording
2. Click **Download**
3. Choose format (MP4)

### Deleting
1. Open recording
2. Click **Delete**
3. Confirm deletion

## Storage & Retention

- Recordings count toward storage quota
- Set retention policies in Settings
- Export before deletion if needed
- Consider local backup for important sessions`,
    'telehealth',
    ['recording', 'record', 'video'],
    ['save', 'capture', 'playback', 'consent'],
    5
  ),
];

// ============================================================================
// CONTENT & TOOLS ARTICLES
// ============================================================================

const contentToolsArticles: HelpArticle[] = [
  createArticle(
    'training-programs',
    'Training Programs',
    'Create and assign training programs to clients',
    `# Training Programs

Create comprehensive training programs to guide your clients' fitness journeys.

## Creating a Program

1. Go to **Content > Training Programs**
2. Click **Create Program**
3. Enter program details:
   - Program name
   - Description
   - Duration (weeks)
   - Difficulty level
   - Target goals

## Program Structure

### Phases
Organize your program into phases:
- Foundation phase
- Building phase
- Peak phase
- Recovery phase

### Weeks
Break down each phase into weeks with specific focus areas.

### Days
Plan daily workouts with:
- Exercise selection
- Sets and reps
- Rest periods
- Notes and modifications

## Adding Exercises

1. Click **Add Exercise**
2. Search the exercise library
3. Or create custom exercise
4. Set parameters:
   - Sets × Reps
   - Weight/resistance
   - Duration
   - Rest between sets

## Exercise Library

Access hundreds of exercises with:
- Video demonstrations
- Written instructions
- Muscle groups targeted
- Equipment needed
- Difficulty rating

## Assigning Programs

1. Open the program
2. Click **Assign to Client**
3. Select client(s)
4. Set start date
5. Configure options:
   - Send notification
   - Include intro message
6. Click **Assign**

## Client Experience

Clients see their program in their portal:
- Weekly overview
- Daily workouts
- Exercise videos
- Logging capability
- Progress tracking`,
    'content-tools',
    ['training', 'programs', 'fitness'],
    ['workout', 'exercise', 'plan', 'assign'],
    1
  ),

  createArticle(
    'forms-assessments',
    'Forms and Assessments',
    'Create custom forms and intake assessments',
    `# Forms and Assessments

Create custom forms for intake, assessments, and data collection.

## Creating a Form

1. Go to **Content > Forms**
2. Click **Create Form**
3. Enter form details:
   - Form name
   - Description
   - Category

## Form Builder

### Question Types
- **Text**: Short or long text answers
- **Number**: Numeric input
- **Single Choice**: Radio buttons
- **Multiple Choice**: Checkboxes
- **Scale**: Rating scale (1-5, 1-10)
- **Date**: Date picker
- **File Upload**: Document/image upload

### Question Options
- Required/optional
- Help text
- Conditional logic
- Input validation

## Form Templates

Start with templates for common needs:
- Health history intake
- Nutrition assessment
- Fitness evaluation
- Goal setting
- Progress check-in
- Session feedback

## Conditional Logic

Show/hide questions based on answers:
1. Select a question
2. Click **Add Logic**
3. Set conditions:
   - "If Question 1 = Yes"
   - "Show Question 2"
4. Create branching paths

## Sending Forms

### To Individual Client
1. Open the form
2. Click **Send to Client**
3. Select client
4. Add message (optional)
5. Set due date (optional)

### During Intake
Forms can be part of client onboarding:
1. Go to **Settings > Client Onboarding**
2. Add forms to intake sequence
3. New clients receive automatically

## Viewing Responses

1. Go to **Content > Forms**
2. Select a form
3. Click **Responses**
4. View individual or aggregate data
5. Export to CSV/PDF`,
    'content-tools',
    ['forms', 'assessments', 'intake'],
    ['questionnaire', 'survey', 'questions', 'data collection'],
    2
  ),

  createArticle(
    'recipes-meal-plans',
    'Recipes and Meal Plans',
    'Create and share recipes and meal plans with clients',
    `# Recipes and Meal Plans

Create nutritious recipes and comprehensive meal plans for your clients.

## Creating Recipes

1. Go to **Content > Recipes**
2. Click **Create Recipe**
3. Enter recipe details:
   - Recipe name
   - Description
   - Prep time / Cook time
   - Servings
   - Difficulty

### Ingredients
1. Click **Add Ingredient**
2. Enter:
   - Ingredient name
   - Amount
   - Unit
   - Notes (optional)
3. Nutrition auto-calculates

### Instructions
Write step-by-step instructions:
- Number each step
- Keep steps clear and simple
- Add timing notes
- Include tips

### Nutrition Information
Auto-calculated from ingredients:
- Calories
- Protein / Carbs / Fat
- Fiber
- Key vitamins/minerals

### Tags & Categories
Organize with tags:
- Meal type (breakfast, lunch, dinner)
- Dietary (vegan, gluten-free, keto)
- Cuisine type
- Season

## Creating Meal Plans

1. Go to **Content > Meal Plans**
2. Click **Create Meal Plan**
3. Set plan parameters:
   - Duration (days/weeks)
   - Meals per day
   - Calorie target
   - Dietary restrictions

### Building the Plan
1. Add meals for each day
2. Drag and drop to rearrange
3. Copy days/weeks
4. Auto-fill with suggestions

### AI Meal Planning
Premium feature:
1. Click **Generate with AI**
2. Set preferences
3. AI creates balanced plan
4. Review and customize

## Assigning to Clients

1. Open meal plan or recipe
2. Click **Share with Client**
3. Select client(s)
4. Add personal message
5. Client receives in portal`,
    'content-tools',
    ['recipes', 'meals', 'nutrition'],
    ['food', 'meal plan', 'diet', 'cooking'],
    3
  ),

  createArticle(
    'content-library',
    'Content Library',
    'Organize and manage your educational resources',
    `# Content Library

Organize all your educational content in one central library.

## Library Organization

### Folders
Create folders for organization:
1. Click **New Folder**
2. Name the folder
3. Drag content into folders
4. Create subfolders as needed

### Categories
Pre-built categories:
- Training Programs
- Meal Plans
- Recipes
- Forms
- Documents
- Videos
- Images

## Uploading Content

1. Go to **Content > Library**
2. Click **Upload**
3. Select files or drag and drop
4. Add metadata:
   - Title
   - Description
   - Tags
   - Category
5. Set visibility

## Content Types

### Documents
Upload PDFs, guides, handouts:
- Educational materials
- Reference guides
- Handouts for clients

### Videos
- Exercise demonstrations
- Educational content
- Tutorial videos
- Supports YouTube/Vimeo links

### Images
- Progress photo storage
- Infographics
- Diagrams

## Sharing Content

### With Individual Client
1. Select content item
2. Click **Share**
3. Choose client
4. Add message
5. Send

### In Messages
1. Open message conversation
2. Click attachment icon
3. Select **From Library**
4. Choose content
5. Send

### In Programs/Plans
Link library content to:
- Training programs
- Meal plans
- Forms

## Search & Filter

Find content quickly:
- Search by name or keyword
- Filter by type
- Filter by category
- Sort by date or name`,
    'content-tools',
    ['library', 'resources', 'content'],
    ['organize', 'upload', 'documents', 'videos'],
    4
  ),

  createArticle(
    'report-templates',
    'Report Templates',
    'Create and customize client report templates',
    `# Report Templates

Create professional reports to track and share client progress.

## Report Types

### Progress Reports
Track client improvements:
- Goal achievement
- Measurements over time
- Activity completion
- Trends and patterns

### Assessment Reports
Summarize evaluation results:
- Initial assessment
- Follow-up comparisons
- Recommendations

### Session Summaries
Document appointment outcomes:
- Session notes compilation
- Action items
- Progress markers

## Creating Templates

1. Go to **Content > Report Templates**
2. Click **Create Template**
3. Choose template type
4. Build your template

### Template Builder

**Header Section**
- Practice logo
- Report title
- Client information
- Date range

**Content Sections**
Add sections for:
- Goals summary
- Progress charts
- Metrics tables
- Notes and observations
- Recommendations

**Charts & Visualizations**
Include:
- Line charts (trends)
- Bar charts (comparisons)
- Progress bars
- Before/after images

## Generating Reports

1. Go to client profile
2. Click **Generate Report**
3. Select template
4. Choose date range
5. Preview and customize
6. Generate PDF

## Sharing Reports

### With Client
- Share via message
- Add to client portal
- Email directly

### Export Options
- PDF download
- Print-friendly view
- Save to library`,
    'content-tools',
    ['reports', 'templates', 'progress'],
    ['generate', 'pdf', 'summary', 'documentation'],
    5
  ),

  createArticle(
    'group-sessions-content',
    'Group Sessions',
    'Manage and run group coaching sessions',
    `# Group Sessions

Run effective group coaching sessions with multiple clients.

## Creating a Group Session

1. Go to **Calendar**
2. Click **New Appointment**
3. Select **Group Session**
4. Configure session:
   - Session name
   - Date and time
   - Duration
   - Maximum participants
   - Description

## Participant Management

### Adding Participants
1. Open the group session
2. Click **Add Participants**
3. Select clients or search
4. Send invitations

### Waitlist
If session is full:
- Enable waitlist option
- Clients can join waitlist
- Auto-notify when spot opens

### Attendance
Track who attends:
- Mark attendance during session
- Track no-shows
- View attendance history

## Running Group Sessions

### Video Groups
For online group sessions:
- All participants in one call
- Screen sharing available
- Chat for all participants
- Recording option

### In-Person Groups
For physical sessions:
- Print participant list
- Check-in functionality
- Note-taking during session

## Group Content

### Shared Resources
All participants access:
- Session materials
- Handouts
- Recordings (if enabled)

### Group Discussions
Enable group messaging:
- Participants can interact
- You moderate discussions
- Share tips and support

## Pricing Groups

Set group session pricing:
- Per session rate
- Package deals
- Membership access`,
    'content-tools',
    ['groups', 'sessions', 'classes'],
    ['group coaching', 'class', 'workshop', 'multiple clients'],
    6
  ),

  createArticle(
    'exercise-library',
    'Exercise Library',
    'Access and customize the exercise database',
    `# Exercise Library

Access hundreds of exercises with video demonstrations and detailed instructions.

## Browsing Exercises

1. Go to **Content > Exercise Library**
2. Browse by:
   - Muscle group
   - Equipment
   - Difficulty
   - Movement type

## Exercise Details

Each exercise includes:
- HD video demonstration
- Written instructions
- Target muscles
- Equipment needed
- Difficulty rating
- Common mistakes
- Modifications

## Search & Filter

### Quick Search
Type exercise name or keyword.

### Filters
- **Muscle Group**: Chest, back, legs, etc.
- **Equipment**: Bodyweight, dumbbells, machines
- **Difficulty**: Beginner to advanced
- **Type**: Strength, cardio, flexibility

## Custom Exercises

Create your own exercises:
1. Click **Create Exercise**
2. Enter details:
   - Exercise name
   - Description
   - Instructions
   - Target muscles
3. Upload video (optional)
4. Save to your library

## Using in Programs

Add exercises to training programs:
1. Open program builder
2. Click **Add Exercise**
3. Search or browse library
4. Select exercise
5. Set parameters (sets, reps, etc.)

## Exercise Favorites

Mark frequently used exercises:
1. Open exercise
2. Click **Favorite** (star icon)
3. Access favorites from filter

## Video Recording

Record custom exercise demos:
1. Click **Record Video**
2. Use webcam or phone
3. Trim and edit
4. Attach to exercise`,
    'content-tools',
    ['exercises', 'library', 'fitness'],
    ['workout', 'movements', 'database', 'video'],
    7
  ),

  createArticle(
    'ai-features',
    'AI-Powered Features',
    'Use AI tools to enhance your practice',
    `# AI-Powered Features

Leverage artificial intelligence to enhance your coaching practice.

## AI Meal Planning

Generate personalized meal plans:
1. Go to **Content > Meal Plans**
2. Click **Generate with AI**
3. Set parameters:
   - Calorie target
   - Dietary restrictions
   - Preferences
   - Duration
4. AI creates balanced plan
5. Review and customize

## AI Workout Suggestions

Get exercise recommendations:
1. Open program builder
2. Click **AI Suggestions**
3. Specify:
   - Target muscles
   - Equipment available
   - Client fitness level
4. Review suggestions
5. Add to program

## Smart Scheduling

AI helps optimize your calendar:
- Suggests best times for appointments
- Identifies scheduling patterns
- Recommends buffer times
- Predicts no-show risk

## Client Insights

AI analyzes client data:
- Progress predictions
- Risk factors
- Engagement patterns
- Recommended interventions

## Content Recommendations

AI suggests content for clients:
- Based on goals
- Based on progress
- Based on preferences
- Similar to successful clients

## AI Writing Assistant

Help with content creation:
- Session notes suggestions
- Message drafting
- Report summaries
- Form question ideas

## Data Requirements

AI features work best with:
- Complete client profiles
- Regular progress logging
- Historical data
- Consistent usage

## Privacy & AI

Your data with AI:
- Processed securely
- Not shared externally
- Used only for your practice
- Can be disabled if preferred`,
    'content-tools',
    ['ai', 'artificial intelligence', 'smart'],
    ['generate', 'suggestions', 'automated', 'machine learning'],
    8
  ),

  createArticle(
    'sharing-content',
    'Sharing Content with Clients',
    'Share resources and materials with your clients',
    `# Sharing Content with Clients

Effectively share resources, programs, and materials with your clients.

## Sharing Methods

### Direct Share
1. Open any content item
2. Click **Share**
3. Select client(s)
4. Add message (optional)
5. Click **Send**

### Via Messages
1. Open conversation
2. Click attachment icon
3. Choose **From Library**
4. Select content
5. Send message

### Client Portal
Automatically available:
- Assigned programs
- Assigned meal plans
- Shared documents
- Session recordings (if shared)

## What Can Be Shared

### Training Programs
- Full program access
- Daily workouts
- Exercise videos
- Progress logging

### Meal Plans
- Complete meal schedule
- Recipes with instructions
- Shopping lists
- Nutrition information

### Documents
- PDFs and guides
- Educational materials
- Handouts
- Custom resources

### Recipes
- Individual recipes
- Collections
- Nutrition data

### Forms
- Assessments
- Check-ins
- Intake forms

## Client Experience

When content is shared, clients:
1. Receive notification
2. See in their portal
3. Can access anytime
4. Track their progress

## Tracking Engagement

See how clients interact:
- View counts
- Completion rates
- Time spent
- Downloads

## Revoking Access

Remove shared content:
1. Go to client profile
2. Select **Shared Content**
3. Click **Remove** on item
4. Client loses access`,
    'content-tools',
    ['sharing', 'send', 'distribute'],
    ['share', 'assign', 'client access', 'portal'],
    9
  ),
];

// ============================================================================
// BILLING ARTICLES
// ============================================================================

const billingArticles: HelpArticle[] = [
  createArticle(
    'managing-subscription',
    'Managing Your Subscription',
    'View and manage your billing and subscription',
    `# Managing Your Subscription

Manage your HealioSpace subscription and billing details.

## Viewing Your Plan

1. Go to **Settings**
2. Select **Billing** tab
3. See current plan details:
   - Plan name
   - Monthly/annual billing
   - Next billing date
   - Price

## Subscription Plans

### Free
- Up to 5 clients
- Basic features
- Standard support

### Plus ($29/month)
- Up to 25 clients
- Telehealth included
- Custom forms
- Email support

### Premium ($79/month)
- Up to 100 clients
- All Plus features
- AI-powered features
- Priority support
- Custom branding

### Enterprise (Custom)
- Unlimited clients
- All features
- Dedicated support
- API access
- Custom contracts

## Upgrading

1. Go to **Settings > Billing**
2. Click **Change Plan**
3. Select new plan
4. Review changes
5. Confirm payment
6. New features available immediately

## Downgrading

1. Go to **Settings > Billing**
2. Click **Change Plan**
3. Select lower tier
4. Review limitations
5. Confirm change
6. Takes effect next billing cycle

Note: Ensure you're within client limits before downgrading.

## Cancellation

1. Go to **Settings > Billing**
2. Click **Cancel Subscription**
3. Complete feedback (optional)
4. Confirm cancellation
5. Access continues until period ends
6. Data retained for 90 days`,
    'billing',
    ['subscription', 'plan', 'billing'],
    ['payment', 'price', 'upgrade', 'downgrade', 'cancel'],
    1
  ),

  createArticle(
    'payment-methods',
    'Payment Methods',
    'Manage your payment methods and billing information',
    `# Payment Methods

Manage how you pay for your HealioSpace subscription.

## Adding Payment Method

1. Go to **Settings > Billing**
2. Click **Payment Methods**
3. Click **Add Payment Method**
4. Enter card details:
   - Card number
   - Expiration date
   - CVC
   - Billing address
5. Click **Save**

## Accepted Payment Methods

### Credit Cards
- Visa
- Mastercard
- American Express
- Discover

### Debit Cards
Most debit cards with credit card logos accepted.

### Bank Transfer
Enterprise plans only:
- ACH transfer (US)
- Wire transfer
- Invoice payment

## Default Payment Method

Set your default card:
1. Go to **Payment Methods**
2. Find the card
3. Click **Set as Default**
4. This card will be charged

## Updating Payment Method

1. Go to **Settings > Billing**
2. Click **Payment Methods**
3. Find the card to update
4. Click **Edit**
5. Update details
6. Save changes

## Removing Payment Method

1. Go to **Payment Methods**
2. Find the card
3. Click **Remove**
4. Confirm removal

Note: You must have at least one valid payment method for paid plans.

## Payment Security

Your payment information is:
- Encrypted with TLS
- Stored securely by Stripe
- Never stored on our servers
- PCI DSS compliant`,
    'billing',
    ['payment', 'card', 'method'],
    ['credit card', 'debit', 'bank', 'pay'],
    2
  ),

  createArticle(
    'invoices-receipts',
    'Invoices and Receipts',
    'Access and download your billing history',
    `# Invoices and Receipts

Access your complete billing history and download invoices.

## Viewing Billing History

1. Go to **Settings > Billing**
2. Click **Billing History**
3. See all past transactions

## Invoice Details

Each invoice shows:
- Invoice number
- Date
- Amount
- Payment status
- Plan details
- Payment method used

## Downloading Invoices

### Individual Invoice
1. Go to **Billing History**
2. Find the invoice
3. Click **Download PDF**
4. Save to your device

### Bulk Download
1. Go to **Billing History**
2. Select date range
3. Click **Download All**
4. Receive ZIP file

## Email Receipts

Receipts are automatically emailed:
- After each successful payment
- Sent to account email
- Includes PDF attachment

### Change Receipt Email
1. Go to **Settings > Billing**
2. Find **Billing Email**
3. Enter new email
4. Save changes

## Invoice Information

### Company Details
Add your business info to invoices:
1. Go to **Settings > Billing**
2. Click **Invoice Settings**
3. Enter:
   - Company name
   - Tax ID (optional)
   - Billing address
4. Save

### Custom Fields
Add custom fields like:
- PO number
- Cost center
- Department

## Tax Information

Invoices include:
- Subtotal
- Tax (where applicable)
- Total amount
- Tax ID (if provided)`,
    'billing',
    ['invoices', 'receipts', 'history'],
    ['download', 'pdf', 'bill', 'statement'],
    3
  ),

  createArticle(
    'billing-faq',
    'Billing FAQ',
    'Common questions about billing and payments',
    `# Billing FAQ

Answers to common billing questions.

## When am I charged?

Subscriptions are billed:
- **Monthly**: Same date each month
- **Annual**: Same date each year
- First charge on signup
- Prorated if upgrading mid-cycle

## Can I get a refund?

### Within 14 days
Full refund available for:
- New subscriptions
- Recent upgrades
- Unsatisfied with service

### After 14 days
Partial refunds considered for:
- Service issues
- Billing errors
- Special circumstances

Contact support for refund requests.

## What happens if payment fails?

1. We'll retry the charge
2. You'll receive email notification
3. 7-day grace period
4. Service paused if not resolved
5. Data retained for 30 days

## Can I pause my subscription?

Yes, on Plus and above:
1. Go to **Settings > Billing**
2. Click **Pause Subscription**
3. Choose duration (1-3 months)
4. Confirm pause
5. Client data is preserved

## How do I update my billing email?

1. Go to **Settings > Billing**
2. Find **Billing Email**
3. Enter new email
4. Verify the change
5. Future receipts go to new email

## Is my payment information secure?

Yes:
- SSL/TLS encryption
- PCI DSS compliant
- Processed by Stripe
- Never stored on our servers

## Can I pay annually?

Yes, save 20% with annual billing:
1. Go to **Settings > Billing**
2. Click **Switch to Annual**
3. See discounted price
4. Confirm change

## How do I cancel?

1. Go to **Settings > Billing**
2. Click **Cancel Subscription**
3. Complete brief survey
4. Confirm cancellation
5. Access until period ends`,
    'billing',
    ['faq', 'questions', 'help'],
    ['refund', 'cancel', 'charge', 'payment failed'],
    4
  ),

  createArticle(
    'enterprise-billing',
    'Enterprise Billing',
    'Information for enterprise and multi-practitioner billing',
    `# Enterprise Billing

Special billing features for enterprise accounts.

## Enterprise Pricing

Enterprise plans include:
- Custom pricing based on needs
- Volume discounts
- Multi-year agreements
- Negotiated terms

Contact sales@healiospace.com for quotes.

## Payment Options

### Invoice Payment
- Net 30 terms available
- Receive monthly invoices
- Pay by check or transfer
- Automatic payment optional

### Bank Transfer
- ACH (US accounts)
- Wire transfer (international)
- Bank details provided on invoice

### Purchase Orders
- Submit PO for billing
- Add PO number to invoices
- Track against PO

## Multi-Practitioner Billing

### Centralized Billing
- One bill for all practitioners
- Itemized by user
- Admin manages payment

### Individual Billing
- Each practitioner pays
- Separate subscriptions
- Individual invoices

### Hybrid
- Base fee centralized
- Per-seat charges separate

## Billing Admin

Designate billing administrators:
1. Go to **Settings > Team**
2. Select user
3. Enable **Billing Admin**
4. They can:
   - View invoices
   - Update payment
   - Manage subscription

## Usage Reports

Enterprise accounts get:
- Monthly usage reports
- Per-practitioner breakdown
- Client counts
- Feature utilization
- Storage usage

## Contract Management

- Annual agreements
- Auto-renewal options
- Advance notice for changes
- Dedicated account manager

## HIPAA BAA

Enterprise includes:
- Business Associate Agreement
- Required for HIPAA compliance
- Signed during onboarding
- Annual renewal`,
    'billing',
    ['enterprise', 'business', 'organization'],
    ['invoice', 'multi-user', 'volume', 'contract'],
    5
  ),
];

// ============================================================================
// SETTINGS & SECURITY ARTICLES
// ============================================================================

const settingsSecurityArticles: HelpArticle[] = [
  createArticle(
    'account-settings',
    'Account Settings',
    'Manage your personal account settings',
    `# Account Settings

Configure your personal account preferences.

## Profile Settings

1. Go to **Settings**
2. Select **Profile** tab

### Personal Information
Update your:
- Full name
- Email address
- Phone number
- Profile photo
- Time zone

### Professional Details
- Title/credentials
- Specialty areas
- Bio/description
- License numbers

## Email Preferences

### Account Email
- Login and security notifications
- Billing notifications
- Product updates

### Marketing
- Newsletter subscription
- Feature announcements
- Tips and best practices

Manage in **Settings > Notifications > Email**.

## Password

### Changing Password
1. Go to **Settings > Security**
2. Click **Change Password**
3. Enter current password
4. Enter new password
5. Confirm new password
6. Click **Update**

### Password Requirements
- Minimum 12 characters
- At least one uppercase
- At least one number
- At least one special character

## Language & Region

### Display Language
Choose interface language:
- English
- Spanish (coming soon)
- More languages planned

### Date Format
- MM/DD/YYYY
- DD/MM/YYYY
- YYYY-MM-DD

### Time Format
- 12-hour (AM/PM)
- 24-hour

## Danger Zone

### Deactivate Account
Temporarily disable your account:
- Clients can't access
- Data is preserved
- Reactivate anytime

### Delete Account
Permanently delete:
- All your data removed
- Cannot be undone
- 30-day waiting period`,
    'settings-security',
    ['account', 'settings', 'profile'],
    ['personal', 'information', 'preferences', 'update'],
    1
  ),

  createArticle(
    'two-factor-authentication',
    'Two-Factor Authentication',
    'Enable 2FA for enhanced account security',
    `# Two-Factor Authentication

Add an extra layer of security with two-factor authentication (2FA).

## Why Use 2FA?

Benefits:
- Protects if password is compromised
- Required for HIPAA compliance
- Prevents unauthorized access
- Industry best practice

## Enabling 2FA

1. Go to **Settings > Security**
2. Find **Two-Factor Authentication**
3. Click **Enable**
4. Choose method:
   - Authenticator app (recommended)
   - SMS (backup option)
5. Follow setup steps

## Authenticator App Setup

1. Download an authenticator app:
   - Google Authenticator
   - Authy
   - 1Password
   - Microsoft Authenticator
2. Scan the QR code
3. Enter the 6-digit code
4. Save backup codes

## SMS Setup

1. Enter your phone number
2. Verify with code sent via SMS
3. SMS codes sent at each login

Note: Authenticator apps are more secure than SMS.

## Backup Codes

When enabling 2FA:
1. You receive 10 backup codes
2. Each code works once
3. Store securely (password manager, safe)
4. Use if you lose your device

## Logging In with 2FA

1. Enter email and password
2. Enter 6-digit code from app/SMS
3. Optionally "Trust this device" for 30 days
4. Access your account

## Lost Access

If you lose your 2FA device:
1. Use a backup code
2. Or contact support
3. Verify your identity
4. Reset 2FA

## Disabling 2FA

1. Go to **Settings > Security**
2. Click **Disable 2FA**
3. Enter your password
4. Confirm disabling

Note: We recommend keeping 2FA enabled.`,
    'settings-security',
    ['2fa', 'authentication', 'security'],
    ['two-factor', 'mfa', 'authenticator', 'login'],
    2
  ),

  createArticle(
    'hipaa-compliance',
    'HIPAA Compliance',
    'Understanding HIPAA compliance in HealioSpace',
    `# HIPAA Compliance

HealioSpace is designed to help you maintain HIPAA compliance.

## What is HIPAA?

The Health Insurance Portability and Accountability Act:
- Protects patient health information (PHI)
- Sets standards for data security
- Requires specific safeguards
- Applies to covered entities

## HealioSpace HIPAA Features

### Technical Safeguards
- End-to-end encryption
- Secure data transmission (TLS)
- Encrypted data at rest
- Access controls

### Administrative Safeguards
- User authentication
- Activity audit logs
- Session timeouts
- Access management

### Physical Safeguards
- Secure data centers
- Redundant backups
- Disaster recovery
- Physical access controls

## Your Responsibilities

As a practitioner, you must:
1. Use strong passwords
2. Enable two-factor authentication
3. Log out when not in use
4. Report security concerns
5. Train staff on HIPAA
6. Maintain BAA with us

## Business Associate Agreement

Enterprise plans include BAA:
- Required for HIPAA compliance
- Defines our responsibilities
- Signed during onboarding

For non-enterprise, contact support for BAA.

## Audit Logs

Track all PHI access:
1. Go to **Settings > Security**
2. Click **Audit Log**
3. View all activities:
   - Login/logout events
   - Data access
   - Changes made
   - Export activities

## Data Retention

Configure retention policies:
- Message retention
- Document retention
- Session recording retention
- Deleted data purging

## Breach Response

If you suspect a breach:
1. Document the incident
2. Contact support immediately
3. We investigate
4. Notify affected parties if required
5. Implement remediation`,
    'settings-security',
    ['hipaa', 'compliance', 'privacy'],
    ['healthcare', 'regulation', 'phi', 'security'],
    3
  ),

  createArticle(
    'practice-settings',
    'Practice Settings',
    'Configure your practice profile and branding',
    `# Practice Settings

Customize your practice profile and branding.

## Practice Profile

1. Go to **Settings**
2. Select **Practice** tab

### Basic Information
- Practice name
- Business address
- Phone number
- Email
- Website

### About Your Practice
- Practice description
- Specialties
- Services offered
- Hours of operation

## Branding

### Logo
Upload your practice logo:
1. Click **Upload Logo**
2. Select image (PNG, JPG)
3. Crop if needed
4. Save

Recommended: 200x200px minimum, square format.

### Colors (Premium)
Customize your brand colors:
- Primary color
- Secondary color
- Accent color

Applied to:
- Client portal
- Email templates
- Reports

### Email Branding (Premium)
Customize email appearance:
- Logo in header
- Brand colors
- Custom footer
- Social links

## Client Portal

Configure client experience:
1. Go to **Settings > Client Portal**

### Portal Name
What clients see when they log in.

### Welcome Message
Custom greeting for clients.

### Available Features
Toggle what clients can access:
- Messaging
- Scheduling
- Documents
- Progress tracking

## Notification Settings

### Practice-Wide
Default notification settings:
- Appointment reminders
- Message alerts
- Billing notifications

### Per-Practitioner
Individual overrides allowed.

## Integrations

Connect third-party services:
- Google Calendar
- Zoom (coming soon)
- Payment processors`,
    'settings-security',
    ['practice', 'branding', 'profile'],
    ['business', 'logo', 'customization', 'portal'],
    4
  ),

  createArticle(
    'notification-settings',
    'Notification Settings',
    'Configure how you receive notifications',
    `# Notification Settings

Control how and when you receive notifications.

## Accessing Settings

1. Go to **Settings**
2. Select **Notifications** tab

## Notification Types

### Appointment Notifications
- New bookings
- Cancellations
- Rescheduling
- Reminders

### Message Notifications
- New messages
- Unread reminders
- Group messages

### Client Notifications
- New client signup
- Form submissions
- Progress updates
- Goal achievements

### System Notifications
- Security alerts
- Billing reminders
- Feature updates

## Delivery Methods

### Email Notifications
- Detailed information
- Can include attachments
- Delivered to account email

### In-App Notifications
- Real-time alerts
- Notification bell icon
- Notification center

### Push Notifications
- Mobile app alerts
- Desktop notifications
- Requires permission

## Configuring Each Type

For each notification:
1. Find the notification type
2. Toggle on/off
3. Select delivery methods
4. Set frequency (immediate/digest)

## Quiet Hours

Set times when notifications are muted:
1. Enable **Quiet Hours**
2. Set start time
3. Set end time
4. Choose days
5. Emergency overrides optional

## Digest Settings

Receive bundled notifications:
- Daily digest
- Weekly summary
- Set preferred time

## Sound Settings

Configure notification sounds:
- Enable/disable sounds
- Choose sound type
- Set volume level`,
    'settings-security',
    ['notifications', 'alerts', 'settings'],
    ['email', 'push', 'reminder', 'sound'],
    5
  ),

  createArticle(
    'data-export',
    'Exporting Your Data',
    'Export your data from HealioSpace',
    `# Exporting Your Data

Export your data from HealioSpace at any time.

## What Can Be Exported

### Client Data
- Client profiles
- Health information
- Progress records
- Notes and documentation

### Content
- Training programs
- Meal plans
- Recipes
- Forms and templates

### Messages
- Conversation history
- Attachments

### Financial
- Billing history
- Invoices
- Payment records

## Export Formats

- **CSV**: Spreadsheet compatible
- **JSON**: Machine readable
- **PDF**: Human readable documents

## Exporting Data

### Full Export
1. Go to **Settings > Data**
2. Click **Export All Data**
3. Select format
4. Confirm request
5. Download when ready (email notification)

### Selective Export
Export specific data:
1. Go to relevant section
2. Use export option
3. Select date range
4. Choose format
5. Download

### Client-Specific Export
For individual client data:
1. Go to client profile
2. Click **More Options**
3. Select **Export Data**
4. Choose what to include
5. Download

## Data Portability

Your data exports include:
- All personal information
- Activity history
- Created content
- Client records

Compliant with:
- GDPR (EU)
- CCPA (California)
- Other privacy regulations

## Processing Time

- Small exports: Immediate
- Medium exports: Few minutes
- Full export: Up to 24 hours

You'll receive email when ready.

## Retention After Export

After exporting:
- Data remains in HealioSpace
- Export doesn't delete anything
- Use delete options separately`,
    'settings-security',
    ['export', 'data', 'download'],
    ['backup', 'csv', 'pdf', 'portability'],
    6
  ),

  createArticle(
    'team-management',
    'Team Management',
    'Manage practitioners and staff in your practice',
    `# Team Management

Manage multiple practitioners and staff members in your practice.

## Adding Team Members

1. Go to **Settings > Team**
2. Click **Invite Team Member**
3. Enter their email
4. Select role
5. Send invitation

## Roles

### Practice Owner
- Full access to everything
- Billing management
- Can delete practice
- Can transfer ownership

### Administrator
- Manage team members
- Access all clients
- Configure settings
- Cannot access billing

### Practitioner
- Access assigned clients
- Manage own calendar
- Use all features
- Limited settings access

### Assistant
- View client schedules
- Send messages
- Limited client access
- Cannot modify records

## Permissions

Customize role permissions:
1. Go to **Settings > Team**
2. Click **Roles & Permissions**
3. Select role
4. Toggle permissions
5. Save changes

## Client Assignment

### Manual Assignment
1. Open client profile
2. Click **Assign Practitioner**
3. Select team member
4. Save

### Automatic Assignment
Configure rules for auto-assignment:
- By specialty
- By availability
- Round-robin

## Team Calendar

View team-wide calendar:
1. Go to **Calendar**
2. Click **Team View**
3. See all practitioners' schedules
4. Filter by team member

## Removing Team Members

1. Go to **Settings > Team**
2. Find the member
3. Click **Remove**
4. Choose what happens to their:
   - Clients (reassign)
   - Appointments (reassign/cancel)
   - Data (retain/delete)
5. Confirm removal`,
    'settings-security',
    ['team', 'staff', 'practitioners'],
    ['multi-user', 'roles', 'permissions', 'admin'],
    7
  ),

  createArticle(
    'integrations-settings',
    'Integrations',
    'Connect HealioSpace with other tools and services',
    `# Integrations

Connect HealioSpace with your favorite tools and services.

## Available Integrations

### Google Calendar
Sync your appointments:
1. Go to **Settings > Integrations**
2. Click **Google Calendar**
3. Sign in with Google
4. Authorize access
5. Choose sync options:
   - One-way (HealioSpace → Google)
   - Two-way sync
   - Select calendars

### Apple Calendar
Sync with Apple Calendar:
1. Go to **Settings > Integrations**
2. Click **Apple Calendar**
3. Follow setup instructions
4. Uses CalDAV protocol

### Zoom (Coming Soon)
Use Zoom for telehealth:
- Connect your Zoom account
- Auto-create meeting links
- Join directly from HealioSpace

### WHOOP
Track client fitness data:
1. Client connects their WHOOP
2. Data syncs automatically
3. View in client profile:
   - Recovery scores
   - Strain data
   - Sleep metrics

### Stripe
Payment processing:
- Already integrated
- Manage in **Settings > Billing**
- Secure payment handling

## API Access (Enterprise)

Build custom integrations:
1. Go to **Settings > Integrations**
2. Click **API Access**
3. Generate API keys
4. View documentation
5. Build your integration

### API Features
- Client management
- Appointment scheduling
- Messaging
- Content access
- Webhooks

## Managing Integrations

### View Status
See all connected integrations:
- Connection status
- Last synced
- Any errors

### Refresh Connection
If sync issues occur:
1. Click on integration
2. Select **Refresh**
3. Re-authorize if needed

### Disconnect
To remove an integration:
1. Click on integration
2. Select **Disconnect**
3. Confirm removal
4. Data already synced remains`,
    'settings-security',
    ['integrations', 'connect', 'sync'],
    ['google', 'zoom', 'api', 'calendar', 'whoop'],
    8
  ),
];

// ============================================================================
// EXPORT ALL ARTICLES
// ============================================================================

export const articles: HelpArticle[] = [
  ...gettingStartedArticles,
  ...clientsArticles,
  ...appointmentsArticles,
  ...messagingArticles,
  ...telehealthArticles,
  ...contentToolsArticles,
  ...billingArticles,
  ...settingsSecurityArticles,
];
